/* eslint-disable no-console */
/* global globalThis */
import React from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

const loaderStyle = `
  @keyframes bta-spin { to { transform: rotate(360deg); } }
  .bta-suspense-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 0;
  }
  .bta-suspense-loader::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid #e0e0e0;
    border-top-color: #637381;
    border-radius: 50%;
    animation: bta-spin 0.7s linear infinite;
  }
  .bta-suspense-skeleton-page {
    padding: 16px 20px 32px;
  }
  .bta-suspense-skeleton-header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }
  .bta-suspense-skeleton-title,
  .bta-suspense-skeleton-button,
  .bta-suspense-skeleton-tab,
  .bta-suspense-skeleton-line {
    background: #ebebeb;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }
  .bta-suspense-skeleton-title::after,
  .bta-suspense-skeleton-button::after,
  .bta-suspense-skeleton-tab::after,
  .bta-suspense-skeleton-line::after {
    animation: bta-skeleton-shimmer 1.2s ease-in-out infinite;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.7), transparent);
    content: '';
    inset: 0;
    position: absolute;
    transform: translateX(-100%);
  }
  @keyframes bta-skeleton-shimmer { to { transform: translateX(100%); } }
  .bta-suspense-skeleton-title {
    height: 28px;
    width: 176px;
  }
  .bta-suspense-skeleton-actions {
    display: flex;
    gap: 8px;
  }
  .bta-suspense-skeleton-button {
    height: 32px;
    width: 112px;
  }
  .bta-suspense-skeleton-tabs {
    display: flex;
    gap: 24px;
    margin-bottom: 12px;
  }
  .bta-suspense-skeleton-tab {
    height: 28px;
    width: 72px;
  }
  .bta-suspense-skeleton-card {
    background: #fff;
    border: 1px solid #e3e3e3;
    border-radius: 12px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
    padding: 16px;
  }
  .bta-suspense-skeleton-line {
    height: 16px;
    margin-bottom: 12px;
  }
  .bta-suspense-skeleton-line:last-child {
    margin-bottom: 0;
  }
`;

const LOADER_DELAY_MS = 200;
const COMPONENT_ATTRIBUTE = 'data-react-component';
const COMPONENT_PROPS_ATTRIBUTE = 'data-react-props';
const VITE_PRELOAD_RELOAD_STORAGE_PREFIX = 'bta:vite-preload-reload:';

export function registerVitePreloadRecovery(browserWindow = window) {
  browserWindow.addEventListener('vite:preloadError', (event) => {
    let storage;
    let storageKey;

    try {
      storage = browserWindow.sessionStorage;
      storageKey = `${VITE_PRELOAD_RELOAD_STORAGE_PREFIX}${browserWindow.location.href}`;

      // Keep the per-URL marker for this tab so persistent failures reach the fallback.
      if (storage.getItem(storageKey)) {
        return;
      }

      storage.setItem(storageKey, 'true');
      browserWindow.location.reload();
      event.preventDefault();
    } catch (_error) {
      try {
        if (storageKey) {
          storage?.removeItem(storageKey);
        }
      } catch (_storageError) {
        // Preserve Vite's original error when storage cleanup also fails.
      }
    }
  });
}

registerVitePreloadRecovery();

const NO_SPINNER_COMPONENTS = new Set(['Layout', 'LayoutContainer']);
const STRUCTURED_SKELETON_LABELS = new Map([
  ['IndexBookingsContainer', ['common.loading.bookings', 'Loading bookings']],
  ['IndexProductsContainer', ['common.loading.services', 'Loading services']],
  ['ProductContainer', ['common.loading.services', 'Loading services']],
  ['ServiceAddons', ['common.loading.services', 'Loading services']],
  ['EditWidget', ['common.loading.widgets', 'Loading widgets']],
  ['EditBookingsContainer', ['common.loading.booking_edit', 'Loading booking editor']],
  ['EditReport', ['common.loading.custom_report_edit', 'Loading custom report editor']],
  ['Settings', ['common.loading.settings', 'Loading settings']],
  ['SettingsPageContainer', ['common.loading.settings', 'Loading settings']],
  ['SearchSettings', ['common.loading.settings', 'Loading settings']],
  ['Branding', ['common.loading.branding', 'Loading branding']],
  ['Staff', ['common.loading.staff', 'Loading staff accounts']],
  ['EditStaff', ['common.loading.staff_edit', 'Loading staff editor']],
  ['Resources', ['common.loading.resources', 'Loading resources']],
  ['EditResource', ['common.loading.resources', 'Loading resources']],
  ['Locations', ['common.loading.locations', 'Loading locations']],
  ['EditLocation', ['common.loading.locations', 'Loading locations']],
]);

function translatedFallback(key, fallback) {
  try {
    const translated = globalThis.I18n?.t?.(key, { defaultValue: fallback });
    return translated && translated !== key ? translated : fallback;
  } catch (_error) {
    return fallback;
  }
}

export function ErrorFallback() {
  const errorFallbackStyle = {
    background: 'var(--p-color-bg-surface-critical, #fff4f4)',
    border: '1px solid var(--p-color-border-critical, #e51c00)',
    borderRadius: '8px',
    color: 'var(--p-color-text-critical, #8e0b21)',
    margin: '16px',
    padding: '24px 16px',
    textAlign: 'center',
    fontSize: '14px',
    lineHeight: '1.5',
  };

  const reloadButtonStyle = {
    background: 'var(--p-color-bg-fill-critical, #c70a24)',
    border: 0,
    borderRadius: '8px',
    color: 'var(--p-color-text-inverse, #fff)',
    cursor: 'pointer',
    fontWeight: 600,
    padding: '8px 16px',
  };

  const supportLinkStyle = {
    color: 'inherit',
    fontWeight: 600,
    textDecoration: 'underline',
  };

  return (
    <div role="alert" style={errorFallbackStyle}>
      <p>
        {translatedFallback(
          'common.loading.error.title',
          'Something went wrong during BookThatApp loading.'
        )}
      </p>
      <p>
        {translatedFallback(
          'common.loading.error.message',
          'Reload the page and try again. If the problem continues,'
        )}{' '}
        <a href="mailto:support@bookthatapp.com" style={supportLinkStyle}>
          {translatedFallback(
            'common.loading.error.contact_support',
            'contact BookThatApp support'
          )}
        </a>
        .
      </p>
      <button onClick={() => window.location.reload()} style={reloadButtonStyle} type="button">
        {translatedFallback('common.loading.error.reload', 'Reload')}
      </button>
    </div>
  );
}

function StructuredSkeletonFallback({ loadingLabel }) {
  return (
    <div className="bta-suspense-skeleton-page" role="status" aria-label={loadingLabel}>
      <div className="bta-suspense-skeleton-tabs">
        <div className="bta-suspense-skeleton-tab" />
        <div className="bta-suspense-skeleton-tab" />
        <div className="bta-suspense-skeleton-tab" />
      </div>
      <div className="bta-suspense-skeleton-header">
        <div className="bta-suspense-skeleton-title" />
        <div className="bta-suspense-skeleton-actions">
          <div className="bta-suspense-skeleton-button" />
          <div className="bta-suspense-skeleton-button" />
        </div>
      </div>
      <div className="bta-suspense-skeleton-card">
        <div className="bta-suspense-skeleton-line" style={{ width: '42%' }} />
        <div className="bta-suspense-skeleton-line" style={{ width: '88%' }} />
        <div className="bta-suspense-skeleton-line" style={{ width: '76%' }} />
        <div className="bta-suspense-skeleton-line" style={{ width: '92%' }} />
        <div className="bta-suspense-skeleton-line" style={{ width: '64%' }} />
      </div>
    </div>
  );
}

function SuspenseFallback({ componentName }) {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setVisible(true), LOADER_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  const loadingLabel = STRUCTURED_SKELETON_LABELS.get(componentName);

  return (
    <>
      <style>{loaderStyle}</style>
      {loadingLabel
        ? <StructuredSkeletonFallback loadingLabel={translatedFallback(...loadingLabel)} />
        : <div className="bta-suspense-loader" />}
    </>
  );
}

// Tracks React 18 roots by DOM element so we can unmount cleanly.
const roots = new WeakMap();

function unmountComponents() {
  const containers = document.querySelectorAll(`[${COMPONENT_ATTRIBUTE}]`);

  containers.forEach((containerElement) => {
    const root = roots.get(containerElement);
    if (root) {
      root.unmount();
      roots.delete(containerElement);
    }
  });
}

function readJsonData(element, attribute) {
  const text = attribute
    ? element?.getAttribute(attribute)
    : element?.textContent;
  const data =
    typeof text === 'string' ? JSON.parse(text.trim() || 'null') : null;
  if (data != null && typeof data === 'object') {
    return data;
  }
  return {};
}

function doMount(registeredComponents) {
  // Retrieve component mount points and their props following this pattern:
  // <div data-react-component="name" data-react-props="<%= some_props.to_json %>"></div>
  const componentMountPoints = document.querySelectorAll(
    `[${COMPONENT_ATTRIBUTE}]`
  );

  componentMountPoints.forEach((containerElement) => {
    const name = containerElement.getAttribute(COMPONENT_ATTRIBUTE);
    const props = readJsonData(containerElement, COMPONENT_PROPS_ATTRIBUTE);

    if (
      name &&
      Object.prototype.hasOwnProperty.call(registeredComponents, name)
    ) {
      const Component = registeredComponents[name];

      let root = roots.get(containerElement);
      if (!root) {
        root = createRoot(containerElement);
        roots.set(containerElement, root);
      }

      root.render(
        <React.StrictMode>
          <ErrorBoundary
            fallback={<ErrorFallback />}
            onError={(error, info) => console.error('BTA: Component rendering error', { error, info, component: name })}
          >
            <React.Suspense
              fallback={NO_SPINNER_COMPONENTS.has(name)
                ? null
                : <SuspenseFallback componentName={name} />}
            >
              <Component {...props} />
            </React.Suspense>
          </ErrorBoundary>
        </React.StrictMode>
      );
    } else if (process.env.NODE_ENV === 'development') {
      console.warn(
        `Cannot mount unknown component '${name}'.\nUse one of: ${Object.keys(
          registeredComponents
        ).join(', ')}.`
      );
    }
  });
}

export default function register(components) {
  const onReady = () => {
    doMount(components);
  };

  document.addEventListener('turbo:before-render', unmountComponents);
  document.addEventListener('turbo:load', onReady);
  document.addEventListener('turbo:frame-load', onReady);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }
}
